import { AfterViewInit, EventEmitter, Input, Output, Directive, inject } from "@angular/core";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Fiat, FiatWallet, Field, PayoutAccount } from "@models";
import { BestFeeCollection } from "@models/best_fee_collection";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { Derivative } from "@models/derivative/currencies/derivative";
import { empty } from "@helpers/utils";
import { CryptoTransaction, FiatTransaction, TradableTransaction, Transaction } from "@models/transaction";
import { AccountService } from "../../../account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";

import { take } from "rxjs";
import { BFFAsset, BFFAssetsService } from "@core-services/bff-assets.service";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";

@Directive()
export abstract class TransactionListCoreComponent implements AfterViewInit {
	@Input()
	public transaction: Transaction;

	@Input()
	public showShareButton: boolean = false;

	@Output()
	public closeEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

	protected payoutAccount: PayoutAccount = null;

	public _fiatWallet: FiatWallet = null;
	public _tradableWallet: TradableWallet = null;
	public _relatedTradableWallet: TradableWallet = null; // Temporary variable; internal_transfer has 2 AssetWallets and both should be stored
	public _tradable: Tradable = null;
	public _recipient: string = "";
	public dividendAssetName: string = "";
	public hasDividendTag: boolean = false;

	protected abstract translationService: TranslationService;
	protected abstract accountService: AccountService;

	private readonly bff: BFFAssetsService = inject(BFFAssetsService);

	private isLoadingBFFAsset: boolean = false;

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService
	) {}

	public ngAfterViewInit(): void {
		if (this.transaction instanceof FiatTransaction) {
			this.payoutAccount = this.payaccountService.list.find(
				(a) => a._id === (<FiatTransaction>this.transaction).getPayoutAccountId()
			);
		}
		this.setTxWallets();

		this.hasDividendTag = this.transaction
			.getTransactionTags()
			.some((element) => element.attributes.short_name.includes("corporate_actions.dividend"));
	}

	private setTxWallets(): void {
		this.fiatWallet = this.getFiatWallet();
		this.tradable = this.tradableListService.getItemById(
			this.transaction.hasTrade() ? this.transaction.getTrade().getCurrencyId() : this.transaction.getCurrencyId()
		);

		const tradableWalletId: string = this.transaction.hasTrade()
			? this.transaction.getTrade().getWalletId()
			: this.transaction.getWalletId();
		this.tradableWallet = this.tradableWalletListService.getItemById(tradableWalletId, true);
		this.relatedTradableWallet = this.getRelatedTradableWallet();
	}

	public getPendingMod(): boolean {
		return (
			this.transaction.getStatus() === "pending" ||
			this.transaction.getStatus() === "processing" ||
			this.transaction.getStatus() === "got_payment"
		);
	}

	public getBestId(): string {
		return TOKEN_BEST_ID;
	}

	public isBestTradingFeeTx(): boolean {
		return this.isBestFeeTx() && !this.storage_fee;
	}

	public isRebalancingTx(): boolean {
		return this.transaction.isRebalancingTx();
	}

	public getFiatWallet(): FiatWallet {
		const fiatId: string = this.transaction.hasTrade()
			? this.transaction.getTrade().getFiatId()
			: this.transaction.getFiatId();
		return this.fiatWalletService.getWalletByFiatId(fiatId);
	}

	private getTradableWallet(): TradableWallet {
		if (this.transaction.hasTrade()) {
			return this.tradableWalletListService.getItemById(this.transaction.getTrade().getWalletId(), true);
		}
		return this.tradableWalletListService.getItemById(this.transaction.getWalletId(), true);
	}

	// Getting the other TradableWallet in case of an internal_transfer
	private getRelatedTradableWallet(): TradableWallet {
		if (this.isInternal) {
			return this.tradableWalletListService.getItemById(
				(this.transaction as TradableTransaction).getRelatedWalletId(),
				true
			);
		}
		return null;
	}

	public getTotalAmount(totalAmount: boolean = true): string {
		if (this.transaction instanceof TradableTransaction) {
			if (totalAmount) {
				return this.transaction.getType() === "deposit"
					? "" + this.transaction.getAmount()
					: this.getCryptoAmount();
			}
			return this.transaction.getType() === "deposit"
				? this.getCryptoAmount()
				: "" + this.transaction.getAmount();
		}
		return "";
	}

	public isOldGiveaway(): boolean {
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().getFiatWalletId().length === 0;
		}
		return false;
	}

	public get fiatWallet(): FiatWallet {
		return this._fiatWallet || this.getFiatWallet();
	}

	public set fiatWallet(wallet: FiatWallet) {
		this._fiatWallet = wallet;
	}

	public get tradableWallet(): TradableWallet {
		return this._tradableWallet || this.getTradableWallet();
	}

	public set tradableWallet(wallet: TradableWallet) {
		this._tradableWallet = wallet;
	}

	public get relatedTradableWallet(): TradableWallet {
		return this._relatedTradableWallet || this.getRelatedTradableWallet();
	}

	public set relatedTradableWallet(wallet: TradableWallet) {
		this._relatedTradableWallet = wallet;
	}

	public get tradable(): Tradable {
		if (!this._tradable) {
			const id = this.transaction.hasTrade()
				? this.transaction.getTrade().getCurrencyId()
				: this.transaction.getCurrencyId();
			this.tradable = this.tradableListService.getItemById(id);

			// We need to fetch this from BFF
			if (!this._tradable && id !== "" && !this.isLoadingBFFAsset) {
				this.isLoadingBFFAsset = true;
				this.bff
					.getAssetFetch({ input: { idInternal: Number(id) } })
					.pipe(take(1))
					.subscribe((asset: BFFAsset) => {
						const bffAsset = new BFFSecurityAsset(asset);
						this.tradableListService.addItem(bffAsset);
						this.tradable = bffAsset;
						this.isLoadingBFFAsset = false;
					});
			}
		}

		return this._tradable;
	}

	public set tradable(tradable: Tradable) {
		this._tradable = tradable;
	}

	public getBankName(): string {
		if (this.isBankTransfer()) {
			return (this.payoutAccount.getFields().find((f) => f.getName() === "bank_name") || new Field()).getValue();
		}
		return "";
	}

	public get fiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public paidFeesWithBest(): boolean {
		return (
			(this.transaction.hasTrade() && this.transaction.getTrade().getBfcUsed()) ||
			(this.transaction.getIsBfc() &&
				this.transaction instanceof TradableTransaction &&
				this.transaction.hasFees())
		);
		// First part of the OR statement refers to trade-transactions that were made with BFC, second part refers to BEST Trading fee transactions AND metal storage fees that were paid with BFC
	}

	public getCurrencyId(): string {
		if (this.tradable) {
			return this.tradable.getId();
		}
		return "";
	}

	public isBestMetalStorageFee(): boolean {
		return this.transaction.getIsBfc() && this.bfcEntity.getRelatedWalletTransaction().isStorageFee;
	}

	protected get bfcEntity(): BestFeeCollection {
		let bfcEntity: BestFeeCollection = new BestFeeCollection();
		if (this.transaction.getIsBfc()) {
			bfcEntity = this.transaction.getBestFeeCollection();
		}
		return bfcEntity;
	}

	public isBestFeeTx(): boolean {
		return this.transaction.getIsBfc();
	}

	public get isInternal(): boolean {
		return this.transaction.getType() === "internal_transfer";
	}

	public getCryptoAmount(): string {
		if (this.transaction instanceof TradableTransaction) {
			if (this.transaction.hasFees()) {
				return +this.transaction.getAmount() + +this.transaction.getFee() + "";
			}
			return this.transaction.getAmount() + "";
		}
		return "";
	}

	public isExternalCryptoDepositWithFees(): boolean {
		return (
			this.transaction instanceof TradableTransaction &&
			this.transaction.getType() === "deposit" &&
			this.transaction.hasFees()
		);
	}

	public isWithdrawal(): boolean {
		return this.transaction.isWithdrawal();
	}

	public isOutgoing(): boolean {
		return this.transaction.isOutgoing();
	}

	public isVoucherTx(): boolean {
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().isVoucher;
		}
		return false;
	}

	public isBankTransfer(): boolean {
		if (this.payoutAccount) {
			return this.payoutAccount.getPayoutProviderId() === "1";
		}
		return false;
	}

	public containsSwap(): boolean {
		if (this.transaction) {
			if (this.transaction.hasTrade()) {
				return this.transaction.getTrade().getIsSwap();
			}
			return this.transaction.isSwap();
		}
		return false;
	}

	public containsDebitCardRelatedTx(): boolean {
		return !!this.transaction && this.transaction.hasDebitCardRelatedTx();
	}

	public containsSavings(): boolean {
		if (this.transaction) {
			if (this.transaction.hasTrade()) {
				return this.transaction.getTrade().getIsSavings();
			}
			return this.transaction.isSavings;
		}
		return false;
	}

	public get storage_fee(): boolean {
		if (this.transaction instanceof TradableTransaction) {
			return this.transaction.isStorageFee;
		}
		return false;
	}

	public getRecipientFromService(): string {
		if (this.transaction instanceof FiatTransaction) {
			return this.transaction.getRecipient();
		}

		if (this.transaction instanceof TradableTransaction) {
			if (!this.transaction) {
				return "";
			}

			const contact = this.contactService.getItemById(this.transaction.getContactId());

			if ((!contact || contact.isDeleted()) && this.transaction.hasRecipient() && !this.transaction.isRefund()) {
				return this.transaction.getRecipient();
			}

			if ((!contact || contact.isDeleted()) && this.transaction.getFrom().length > 0) {
				return this.transaction.getFrom();
			}

			if (empty(contact)) {
				return "";
			}
			if (!contact.isDeleted()) {
				return contact.getName();
			}

			if (contact.getEmail().length > 0) {
				return contact.getEmail();
			}

			const emailField = contact
				.getFields(true, true)
				.filter((f) => f.getType() === "email" && f.getValue().length > 0);
			if (emailField.length > 0) {
				return emailField[0].getValue();
			}

			return this.translationService.translate("transactions_bitpanda_user");
		}
		return "";
	}

	public getRecipient(): string {
		return this._recipient || this.getRecipientFromService();
	}

	public getRecipientInformation(): string {
		return this.transaction.isOutgoing() || this.isIncomingDividendTransaction()
			? this.transaction.getRecipient()
			: this.transaction.getFrom();
	}

	public isIncomingDividendTransaction(): boolean {
		return (this.transaction.isIncoming() && this.hasDividendTag) || this.isDividend();
	}

	public set recipient(recipient: string) {
		this._recipient = recipient;
	}

	public isDebitCardTx(): boolean {
		return this.transaction.isCard();
	}

	public isDividend(): boolean {
		return this.dividendAssetName.length !== 0;
	}

	public isDebitCardWithdrawal(): boolean {
		return this.isDebitCardTx() && this.transaction.isWithdrawal();
	}

	public isFiatTx(): boolean {
		return this.transaction instanceof FiatTransaction;
	}

	public isFiatDebitCardTx(): boolean {
		return this.isDebitCardTx() && this.isFiatTx();
	}

	public hasSpread(): boolean {
		// Only derivatives should show the spread
		return this.tradable instanceof Derivative && !this.isRealSecurities();
	}

	public isCorporateAction(): boolean {
		const tags = this.transaction.getTransactionTags();
		return tags.some((tag) => tag.attributes.short_name.includes("ca."));
	}

	public isRealSecurities(): boolean {
		if (this.tradable instanceof Derivative) {
			return this.tradable.isRealSecurities();
		}
		return false;
	}

	public isSecurityToken(): boolean {
		return this.tradable instanceof SecurityToken;
	}

	public getISIN(): string {
		if (this.tradable instanceof BFFSecurityAsset) {
			return this.tradable.getIsin();
		}
		return "";
	}

	public getExchange(): string {
		if (this.tradable instanceof BFFSecurityAsset) {
			return this.tradable.getExchange();
		}
		return "";
	}

	public getFeeText(): string {
		return this.hasSpread() ? "spread" : "fees_tx_item";
	}

	public getStockSpread(): number {
		return this.transaction.hasTrade() ? this.transaction.getTrade().getStockSpread() : 0;
	}

	public isTrxCryptoMerger(): boolean {
		const tags = this.transaction.getTransactionTags();

		if (this.transaction instanceof CryptoTransaction) {
			if (this.isInternal && tags.some((tag) => tag.attributes.short_name === "merger_crypto")) {
				return true;
			}
		}
		return false;
	}

	public isTrxMarginTradingClose(): boolean {
		const tags = this.transaction.getTransactionTags();

		if (this.transaction instanceof CryptoTransaction) {
			if (this.isInternal && tags.some((tag) => tag.attributes.short_name === "margin_trading.close")) {
				return true;
			}
		}
		return false;
	}

	public isTrxMarginTradingFee(): boolean {
		const tags = this.transaction.getTransactionTags();

		if (this.transaction instanceof CryptoTransaction) {
			if (tags.some((tag) => tag.attributes.short_name === "margin_trading.fee")) {
				return true;
			}
		}
		return false;
	}

	public isTrxVisionFee(): boolean {
		const tags = this.transaction.getTransactionTags();

		if (this.transaction instanceof CryptoTransaction) {
			if (
				this.transaction.getType() === "transfer" &&
				tags.some((tag) => tag.attributes.short_name === "vsn_fee")
			) {
				return true;
			}
		}
		return false;
	}

	public isTrxMarginTrading(): boolean {
		const tags = this.transaction.getTransactionTags();
		if (this.transaction instanceof FiatTransaction || this.transaction instanceof CryptoTransaction) {
			if (tags.some((tag) => tag.attributes.short_name.includes("margin_trading"))) {
				return true;
			}
		}
		return false;
	}
}
